import type { HotelStatus, RoomStatus } from './hotels';
import type { CallStatus, Department } from './calls';
import type { DevicePlatform, ReceptionistStatus } from './users';
import type {
  BookingStatus,
  GuestAddress,
  GuestDocumentType,
  HotelRoomStatus,
  StaffType,
} from './rooms';
import type {
  HotelService,
  HotelServiceFlags,
  HousekeepingServiceItem,
  LaundryServiceItem,
  MenuImageRef,
  PricingUnit,
  ServiceCatalogItem,
  ServiceCatalogKind,
  ServiceChargeType,
  WifiNetwork,
  WifiSecurityType,
} from './services';
import type {
  AdditionalCharge,
  FeedbackRating,
  RequestItem,
  RequestStatus,
  RequestType,
} from './requests';
import type { OfferDiscount, OfferImageRef, OfferService } from './offers';
import type {
  PlacesToExploreImageRef,
  PlacesToExploreKind,
  TourDiscountType,
  TourDuration,
} from './placesToExplore';

/**
 * Request/response contracts for the callable Cloud Functions.
 * Both clients import these so a backend change breaks the build, not runtime.
 */

/* -------------------------------------------------------------------------- */
/* health                                                                     */
/* -------------------------------------------------------------------------- */

export interface HealthResponse {
  ok: true;
  uid: string;
  role: string;
  serverTimeMs: number;
}

/* -------------------------------------------------------------------------- */
/* registerGuestSession                                                       */
/* -------------------------------------------------------------------------- */

export interface RegisterGuestSessionRequest {
  hotelId: string;
  roomId: string;
  /**
   * The mobile number the guest gave at check-in. Must match a guest on the
   * room's `checkedIn` booking, or the call fails `GUEST_NOT_AUTHENTICATED`.
   * Omitted on a reload: the server then resumes this browser's already
   * verified session, and answers `GUEST_PHONE_REQUIRED` when there is none.
   */
  phone?: string;
}

export interface RegisterGuestSessionResponse {
  hotelId: string;
  hotelName: string;
  hotelStatus: HotelStatus;
  roomId: string;
  roomNumber: string;
  roomStatus: RoomStatus;
  /**
   * The booking's lead guest, for the home page's greeting. Absent when the
   * booking has no name on it; the page then falls back to "Guest".
   *
   * It is the *lead* guest's name, not necessarily this browser's — the phone
   * check matches any guest on the booking. See
   * `GuestSessionDoc.leadGuestName`.
   */
  leadGuestName?: string;
  /** Epoch millis after which the guest must re-register. */
  sessionExpiresAtMs: number;
}

/* -------------------------------------------------------------------------- */
/* setReceptionistPresence                                                    */
/* -------------------------------------------------------------------------- */

export interface SetReceptionistPresenceRequest {
  status: Exclude<ReceptionistStatus, 'BUSY'>;
}

export interface SetReceptionistPresenceResponse {
  receptionistId: string;
  hotelId: string;
  status: ReceptionistStatus;
}

/* -------------------------------------------------------------------------- */
/* registerReceptionistDevice / unregisterReceptionistDevice                  */
/* -------------------------------------------------------------------------- */

export interface RegisterDeviceRequest {
  /** Stable per-install id, so re-registering refreshes instead of duplicating. */
  deviceId: string;
  token: string;
  platform: DevicePlatform;
  appVersion?: string;
}

export interface RegisterDeviceResponse {
  deviceId: string;
  registered: true;
}

export interface UnregisterDeviceRequest {
  deviceId: string;
}

export interface UnregisterDeviceResponse {
  deviceId: string;
  removed: boolean;
}

/* -------------------------------------------------------------------------- */
/* Agora credentials                                                          */
/* -------------------------------------------------------------------------- */

/** Never contains the App Certificate — only a short-lived derived token. */
export interface AgoraCredentials {
  appId: string;
  channelName: string;
  token: string;
  uid: number;
  /** Epoch millis; clients refresh shortly before this. */
  expiresAtMs: number;
}

/* -------------------------------------------------------------------------- */
/* Calls                                                                      */
/* -------------------------------------------------------------------------- */

export interface CreateCallRequest {
  hotelId: string;
  roomId: string;
  department?: Department;
}

export interface CreateCallResponse {
  callId: string;
  hotelId: string;
  status: CallStatus;
  agora: AgoraCredentials;
}

export interface CallIdRequest {
  hotelId: string;
  callId: string;
}

export interface AcceptCallResponse {
  callId: string;
  hotelId: string;
  roomNumber: string;
  status: CallStatus;
  agora: AgoraCredentials;
}

export interface CallActionResponse {
  callId: string;
  status: CallStatus;
  durationSeconds?: number;
}

/** Summary the receptionist app fetches after an FCM wake-up. */
export interface GetCallResponse {
  callId: string;
  hotelId: string;
  hotelName: string;
  roomId: string;
  roomNumber: string;
  department: Department;
  status: CallStatus;
  receptionistId: string | null;
  createdAtMs: number | null;
  ringExpiresAtMs: number | null;
}

/* -------------------------------------------------------------------------- */
/* FCM payloads                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `type` on every push this system sends. The app branches on it, so it is a
 * contract rather than a label.
 *
 * The three call types are *data-only* messages: the payload carries
 * identifiers and nothing else, and the app fetches the call through `getCall`
 * and renders the notification locally — which is how a room number reaches a
 * lock screen without travelling in a push.
 *
 * The rest are ordinary notification messages with a title and a body, because
 * they are informational and there is nothing secret in "Room 205 checked in".
 * That difference matters operationally: a notification message is displayed by
 * Android itself when the app is backgrounded or dead, so these arrive without
 * the app having to be woken and without an authenticated round trip on a
 * handset that may be offline.
 */
export const PUSH_TYPE = {
  INCOMING_CALL: 'INCOMING_CALL',
  CALL_CANCELLED: 'CALL_CANCELLED',
  CALL_TAKEN: 'CALL_TAKEN',
  /** Owner: a receptionist checked a guest in / out. */
  GUEST_CHECKED_IN: 'GUEST_CHECKED_IN',
  GUEST_CHECKED_OUT: 'GUEST_CHECKED_OUT',
  /** Owner: a guest left a review. */
  GUEST_FEEDBACK: 'GUEST_FEEDBACK',
  /** Reception + housekeeping: a guest filed a request. */
  REQUEST_CREATED: 'REQUEST_CREATED',
  /** One housekeeper: this request is now yours. Rings on its own channel. */
  REQUEST_ASSIGNED: 'REQUEST_ASSIGNED',
} as const;

export type PushType = (typeof PUSH_TYPE)[keyof typeof PUSH_TYPE];

/** The call pushes: identifiers only, resolved through `getCall`. */
export const CALL_PUSH_TYPES: readonly PushType[] = [
  PUSH_TYPE.INCOMING_CALL,
  PUSH_TYPE.CALL_CANCELLED,
  PUSH_TYPE.CALL_TAKEN,
];

/**
 * FCM carries identifiers only. No Agora token, no guest PII — the app calls
 * `getCall` over an authenticated channel for the rest.
 */
export interface IncomingCallPushData {
  type: PushType;
  callId: string;
  hotelId: string;
}

/**
 * The `data` block on the informational pushes. Every value is a string —
 * FCM allows nothing else — and the set is deliberately small: enough for the
 * app to know what happened and which document it was, never enough to be the
 * only copy of anything.
 */
export interface ActivityPushData {
  type: PushType;
  hotelId: string;
  /** The notification channel the payload asked Android to use. */
  channelId?: string;
  requestId?: string;
  bookingId?: string;
  feedbackId?: string;
  roomId?: string;
}

/* ========================================================================== */
/* Hotel management callables                                                 */
/* ========================================================================== */

/* -------------------------------------------------------------------------- */
/* completeHotelSetup                                                         */
/* -------------------------------------------------------------------------- */

export interface CompleteHotelSetupRequest {
  name: string;
  address?: string;
  city?: string;
  /** Optional seed list; "+ Add New Room Type" can extend it later. */
  roomType?: string[];
}

export interface CompleteHotelSetupResponse {
  hotelId: string;
  /** Always true: the owner claim only lands after an ID-token refresh. */
  refreshToken: true;
}

/* -------------------------------------------------------------------------- */
/* updateHotelProfile                                                         */
/* -------------------------------------------------------------------------- */

export interface UpdateHotelProfileRequest {
  hotelId: string;
  name: string;
  /** Omitted or blank clears the stored value. */
  address?: string;
  city?: string;
  /** Omitted or blank clears the stored value. Must be an https Google link. */
  googleReviewUrl?: string;
  /** Omitted or empty clears the stored list. */
  contactPhones?: string[];
  /**
   * `{path}` sets the logo, `null` removes it, omitted leaves it alone — the
   * one field where "not sent" and "cleared" differ, because an older client
   * that never heard of logos must not wipe one on every save.
   */
  logo?: { path: string } | null;
}

export interface UpdateHotelProfileResponse {
  hotelId: string;
}

/* -------------------------------------------------------------------------- */
/* setOwnerCallAvailability                                                   */
/* -------------------------------------------------------------------------- */

export interface SetOwnerCallAvailabilityRequest {
  hotelId: string;
  /** true writes the owner an active receptionist document; false clears it. */
  enabled: boolean;
}

export interface SetOwnerCallAvailabilityResponse {
  hotelId: string;
  enabled: boolean;
}

/* -------------------------------------------------------------------------- */
/* addRoomType                                                                */
/* -------------------------------------------------------------------------- */

export interface AddRoomTypeRequest {
  hotelId: string;
  roomType: string;
}

export interface AddRoomTypeResponse {
  hotelId: string;
  /** The full list after the addition, so the caller need not re-read. */
  roomType: string[];
}

/* -------------------------------------------------------------------------- */
/* createRoom / updateRoom / deleteRoom                                       */
/* -------------------------------------------------------------------------- */

/** Path only — see the note on `MediaRef`. No download URL is ever stored. */
export interface RoomMediaInput {
  path: string;
}

export interface CreateRoomRequest {
  hotelId: string;
  roomNumber: string;
  roomType: string;
  roomStatus: HotelRoomStatus;
  roomInfo?: string;
  roomPhotos?: RoomMediaInput[];
  roomVideos?: RoomMediaInput[];
}

export interface UpdateRoomRequest extends Partial<Omit<CreateRoomRequest, 'hotelId'>> {
  hotelId: string;
  roomId: string;
}

export interface RoomMutationResponse {
  hotelId: string;
  roomId: string;
  roomNumber: string;
  roomStatus: HotelRoomStatus;
}

export interface DeleteRoomRequest {
  hotelId: string;
  roomId: string;
}

export interface DeleteRoomResponse {
  hotelId: string;
  roomId: string;
  deleted: true;
}

/* -------------------------------------------------------------------------- */
/* createStaffMember / updateStaffMember                                      */
/* -------------------------------------------------------------------------- */

export interface CreateStaffMemberRequest {
  hotelId: string;
  name: string;
  /** E.164 with the Indian country code: +91XXXXXXXXXX. */
  phone: string;
  staffType: StaffType;
}

export interface CreateStaffMemberResponse {
  hotelId: string;
  staffId: string;
  staffType: StaffType;
  refreshToken: true;
}

export interface UpdateStaffMemberRequest {
  hotelId: string;
  staffId: string;
  name?: string;
  staffType?: StaffType;
  active?: boolean;
}

export interface UpdateStaffMemberResponse {
  hotelId: string;
  staffId: string;
  updated: true;
}

/* -------------------------------------------------------------------------- */
/* checkIn / checkOut                                                         */
/* -------------------------------------------------------------------------- */

/**
 * One adult on the check-in form. Filed in the guest register by `checkIn`;
 * the booking then carries the resulting `guestId`, not these fields.
 */
export interface CheckInGuestInput {
  /** Required for the lead guest only. */
  name?: string | null;
  /** Required for the lead guest only; must normalise (see `normalizeGuestPhone`). */
  phone?: string | null;
  gender?: string | null;
  age?: number | null;
  address?: GuestAddress;
  documents?: Array<{ documentType: GuestDocumentType; path: string }>;
}

export interface CheckInRequest {
  hotelId: string;
  roomId: string;
  /** Epoch millis. Defaults to now when omitted. */
  checkinAtMs?: number;
  /** Epoch millis for the expected checkout date. */
  checkoutDueAtMs?: number;
  adultCount: number;
  childCount: number;
  guests: CheckInGuestInput[];
  /**
   * Caller-generated id, echoed back. Re-sending the same one after a network
   * timeout returns the original booking instead of checking the room in twice.
   */
  idempotencyKey?: string;
}

export interface CheckInResponse {
  hotelId: string;
  bookingId: string;
  roomId: string;
  roomNumber: string;
  status: BookingStatus;
  adultCount: number;
  childCount: number;
  /** True when this call created the booking, false when it replayed one. */
  created: boolean;
}

export interface CheckOutRequest {
  hotelId: string;
  roomId: string;
  /** Optional guard: fail rather than check out a different booking. */
  bookingId?: string;
}

export interface CheckOutResponse {
  hotelId: string;
  bookingId: string;
  roomId: string;
  roomNumber: string;
  status: BookingStatus;
  /** Adults removed from `liveGuests` by this checkout. */
  adultCount: number;
}

/* -------------------------------------------------------------------------- */
/* registerOwnerDevice / unregisterOwnerDevice                                */
/* -------------------------------------------------------------------------- */

export interface RegisterOwnerDeviceRequest extends RegisterDeviceRequest {
  hotelId: string;
}

export interface UnregisterOwnerDeviceRequest extends UnregisterDeviceRequest {
  hotelId: string;
}

/* ========================================================================== */
/* Guest services (Manage → Settings → Services; owner only)                  */
/* ========================================================================== */

/* -------------------------------------------------------------------------- */
/* setHotelServiceEnabled                                                     */
/* -------------------------------------------------------------------------- */

export interface SetHotelServiceEnabledRequest {
  hotelId: string;
  service: HotelService;
  enabled: boolean;
}

export interface SetHotelServiceEnabledResponse {
  hotelId: string;
  service: HotelService;
  enabled: boolean;
}

/* -------------------------------------------------------------------------- */
/* saveServiceItem / deleteServiceItem (housekeeping and laundry catalogues)  */
/* -------------------------------------------------------------------------- */

export interface SaveServiceItemRequest {
  hotelId: string;
  kind: ServiceCatalogKind;
  /** Omitted to add a new service; present to edit the one with this id. */
  serviceId?: string;
  name: string;
  enabled: boolean;
  type: ServiceChargeType;
  /** Required when `type` is paid; ignored (stored as 0) when free. */
  charge?: number;
  /** Laundry only. Ignored for housekeeping. */
  pricingUnit?: PricingUnit;
}

export interface SaveServiceItemResponse {
  hotelId: string;
  kind: ServiceCatalogKind;
  serviceId: string;
  /** The full list after the write, so the caller need not re-read. */
  services: ServiceCatalogItem[];
}

export interface DeleteServiceItemRequest {
  hotelId: string;
  kind: ServiceCatalogKind;
  serviceId: string;
}

export interface DeleteServiceItemResponse {
  hotelId: string;
  kind: ServiceCatalogKind;
  serviceId: string;
  deleted: true;
}

/* -------------------------------------------------------------------------- */
/* saveMenuItem / deleteMenuItem                                              */
/* -------------------------------------------------------------------------- */

export interface SaveMenuItemRequest {
  hotelId: string;
  /** Omitted to add a dish; present to edit the one with this id. */
  itemId?: string;
  name: string;
  /** Must be one of the menu's categories (see `addMenuCategory`). */
  category: string;
  price: number;
  enabled: boolean;
  /** Blank or omitted clears the stored value. */
  description?: string;
  /** Omitted or null clears the stored value. */
  vegetarian?: boolean | null;
  /** Minutes. Omitted, null or 0 clears the stored value. */
  preparationMinutes?: number | null;
  /**
   * Up to `MAX_MENU_ITEM_IMAGES` Storage paths. An empty list clears the
   * photos; *omitted* leaves them alone — a row's enable/disable toggle does
   * not carry the field and must not strip photos the owner spent time on.
   */
  images?: Array<{ path: string }>;
}

export interface SaveMenuItemResponse {
  hotelId: string;
  itemId: string;
  created: boolean;
}

export interface DeleteMenuItemRequest {
  hotelId: string;
  itemId: string;
}

export interface DeleteMenuItemResponse {
  hotelId: string;
  itemId: string;
  deleted: true;
}

/* -------------------------------------------------------------------------- */
/* addMenuCategory / renameMenuCategory / deleteMenuCategory                  */
/* -------------------------------------------------------------------------- */

export interface AddMenuCategoryRequest {
  hotelId: string;
  category: string;
}

export interface RenameMenuCategoryRequest {
  hotelId: string;
  category: string;
  newName: string;
}

export interface DeleteMenuCategoryRequest {
  hotelId: string;
  category: string;
}

export interface MenuCategoriesResponse {
  hotelId: string;
  /** The full list after the change. */
  categories: string[];
}

/* ========================================================================== */
/* Guest web: services catalogue, requests and feedback                       */
/* ========================================================================== */

/* -------------------------------------------------------------------------- */
/* getGuestServices                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Everything the guest home and service pages need, in one round trip, for a
 * guest with a live session on this room. Guests cannot read the hotel or
 * settings documents directly — Security Rules keep those to staff — so the
 * catalogue is served the way the hotel name already is: by a callable that
 * validates the session first. Only `enabled` rows are returned.
 */
export interface GetGuestServicesRequest {
  hotelId: string;
  roomId: string;
}

export interface GuestMenuItem {
  id: string;
  name: string;
  category: string;
  price: number;
  description?: string;
  vegetarian?: boolean;
  preparationMinutes?: number;
  images?: MenuImageRef[];
}

/** A network as the guest sees it: no room list, no id the guest can act on. */
export interface GuestWifiNetwork {
  id: string;
  ssid: string;
  password: string;
  securityType: WifiSecurityType;
  additionalInfo?: string;
}

/** A store product as the guest sees it. */
export interface GuestStoreItem {
  id: string;
  name: string;
  category: string;
  price: number;
  description?: string;
  images?: MenuImageRef[];
}

/** A banner as the guest sees it. Display only — nothing here prices an order. */
export interface GuestOffer {
  id: string;
  name: string;
  info: string;
  discount: OfferDiscount;
  service: OfferService;
  badge?: string;
  image?: OfferImageRef;
}

export interface GetGuestServicesResponse {
  hotelId: string;
  flags: HotelServiceFlags;
  /**
   * The hotel's public review link, when one is set on the profile. Sent so
   * the feedback page can route happy guests to Google instead of asking them
   * to type a comment; absent when the hotel has not set one.
   */
  googleReviewUrl?: string;
  /** The `checkedIn` booking for the room, when the hotel has one on record. */
  bookingId: string | null;
  housekeeping: HousekeepingServiceItem[];
  laundry: LaundryServiceItem[];
  menu: {
    categories: string[];
    items: GuestMenuItem[];
  };
  store: {
    categories: string[];
    items: GuestStoreItem[];
  };
  wifi: {
    /** Enabled networks whose applicable rooms include the guest's room. */
    networks: GuestWifiNetwork[];
    /** Whether the hotel has any enabled network at all — tells the two empty states apart. */
    anyConfigured: boolean;
  };
  /**
   * Enabled offers whose service is on (or hotel-wide), in carousel order.
   * Empty when there are none; the home page then shows no carousel.
   */
  offers: GuestOffer[];
}

/* -------------------------------------------------------------------------- */
/* createServiceRequest                                                       */
/* -------------------------------------------------------------------------- */

export interface CreateServiceRequestItemInput {
  /** Catalogue id: a housekeeping/laundry service id or a menu item id. */
  id: string;
  /** Ignored for laundry (staff settle the quantity). */
  quantity?: number;
}

export interface CreateServiceRequestRequest {
  hotelId: string;
  roomId: string;
  type: RequestType;
  /**
   * May be empty for housekeeping when `note` says what is needed ("Can't
   * find what you need?"); required otherwise.
   */
  items: CreateServiceRequestItemInput[];
  note?: string;
  /**
   * Caller-generated, and used as the request id. A second tap on "Place
   * Order" with the same key returns the request already created instead of
   * creating another — the whole duplicate-order guard is this one field.
   */
  idempotencyKey: string;
}

export interface CreateServiceRequestResponse {
  hotelId: string;
  requestId: string;
  type: RequestType;
  status: RequestStatus;
  items: RequestItem[];
  totalCharge: number;
  /** False when the idempotency key matched an existing request. */
  created: boolean;
}

/* -------------------------------------------------------------------------- */
/* updateRequestStatus (staff) / cancelServiceRequest (guest)                 */
/* -------------------------------------------------------------------------- */

export interface UpdateRequestStatusRequest {
  hotelId: string;
  requestId: string;
  status: RequestStatus;
  /** Optional line to the guest, shown with the status change. */
  message?: string;
}

/* -------------------------------------------------------------------------- */
/* assignRequest / updateRequestCharges (staff)                               */
/* -------------------------------------------------------------------------- */

export interface AssignRequestRequest {
  hotelId: string;
  requestId: string;
  /** A housekeeping staff uid, or null to unassign. */
  staffId: string | null;
}

export interface AssignRequestResponse {
  hotelId: string;
  requestId: string;
  assignedTo: string | null;
  assignedToName: string | null;
}

/** One existing line with the quantity / unit charge staff settled on. */
export interface RequestLineEdit {
  id: string;
  quantity: number;
  unitCharge: number;
}

export interface UpdateRequestChargesRequest {
  hotelId: string;
  requestId: string;
  /** Every line, with its final quantity and unit charge. Omitted lines are left as stored. */
  items?: RequestLineEdit[];
  /** The full list of extras; replaces what is stored. Omit to leave untouched. */
  additionalCharges?: Array<{ id?: string; name: string; amount: number }>;
}

export interface UpdateRequestChargesResponse {
  hotelId: string;
  requestId: string;
  items: RequestItem[];
  additionalCharges: AdditionalCharge[];
  totalCharge: number;
}

export interface CancelServiceRequestRequest {
  hotelId: string;
  roomId: string;
  requestId: string;
}

export interface RequestStatusResponse {
  hotelId: string;
  requestId: string;
  status: RequestStatus;
}

/* -------------------------------------------------------------------------- */
/* submitFeedback                                                             */
/* -------------------------------------------------------------------------- */

export interface SubmitFeedbackRequest {
  hotelId: string;
  roomId: string;
  rating: FeedbackRating;
  comment?: string;
}

export interface SubmitFeedbackResponse {
  hotelId: string;
  feedbackId: string;
}

/* -------------------------------------------------------------------------- */
/* saveWifiNetwork / deleteWifiNetwork (owner)                                */
/* -------------------------------------------------------------------------- */

export interface SaveWifiNetworkRequest {
  hotelId: string;
  /** Omitted to add; present to edit the network with this id. */
  networkId?: string;
  ssid: string;
  /** Required unless `securityType` is Open; ignored (stored empty) when it is. */
  password?: string;
  securityType: WifiSecurityType;
  additionalInfo?: string;
  /** Room ids. At least one. */
  applicableRooms: string[];
  enabled: boolean;
}

export interface SaveWifiNetworkResponse {
  hotelId: string;
  networkId: string;
  networks: WifiNetwork[];
}

export interface DeleteWifiNetworkRequest {
  hotelId: string;
  networkId: string;
}

export interface DeleteWifiNetworkResponse {
  hotelId: string;
  networkId: string;
  deleted: true;
}

/* -------------------------------------------------------------------------- */
/* Hotel store: saveStoreItem / deleteStoreItem / *StoreCategory (owner)       */
/* -------------------------------------------------------------------------- */

/** Same contract as a menu item, minus the dish-only fields. */
export interface SaveStoreItemRequest {
  hotelId: string;
  itemId?: string;
  name: string;
  category: string;
  price: number;
  enabled: boolean;
  description?: string;
  images?: Array<{ path: string }>;
}

export type SaveStoreItemResponse = SaveMenuItemResponse;
export type DeleteStoreItemRequest = DeleteMenuItemRequest;
export type DeleteStoreItemResponse = DeleteMenuItemResponse;
export type AddStoreCategoryRequest = AddMenuCategoryRequest;
export type RenameStoreCategoryRequest = RenameMenuCategoryRequest;
export type DeleteStoreCategoryRequest = DeleteMenuCategoryRequest;
export type StoreCategoriesResponse = MenuCategoriesResponse;

/* -------------------------------------------------------------------------- */
/* Offers: saveOffer / setOfferEnabled / reorderOffers / deleteOffer (owner)  */
/* -------------------------------------------------------------------------- */

export interface SaveOfferRequest {
  hotelId: string;
  /** Omitted to add; present to edit the offer with this id. */
  offerId?: string;
  name: string;
  info: string;
  discount: OfferDiscount;
  service: OfferService;
  /** Blank or omitted clears the stored value; the client then derives one. */
  badge?: string;
  /**
   * The banner background as a Storage path. `null` clears it; *omitted*
   * leaves it alone, so a save that does not carry the field cannot strip a
   * photo the owner uploaded.
   */
  image?: OfferImageRef | null;
  enabled: boolean;
}

export interface SaveOfferResponse {
  hotelId: string;
  offerId: string;
  created: boolean;
}

export interface SetOfferEnabledRequest {
  hotelId: string;
  offerId: string;
  enabled: boolean;
}

export interface SetOfferEnabledResponse {
  hotelId: string;
  offerId: string;
  enabled: boolean;
}

export interface ReorderOffersRequest {
  hotelId: string;
  /** Every offer id, in the order the carousel should show them. */
  offerIds: string[];
}

export interface ReorderOffersResponse {
  hotelId: string;
  offerIds: string[];
}

export interface DeleteOfferRequest {
  hotelId: string;
  offerId: string;
}

export interface DeleteOfferResponse {
  hotelId: string;
  offerId: string;
  deleted: true;
}

/* -------------------------------------------------------------------------- */
/* Places to Explore (owner): savePlace / saveTourPackage and friends         */
/* -------------------------------------------------------------------------- */

export interface SavePlaceRequest {
  hotelId: string;
  /** Omitted to add; present to edit the place with this id. */
  placeId?: string;
  name: string;
  locationDetails: string;
  /** Blank or omitted clears the stored value. */
  shortDescription?: string;
  /** Kilometres from the hotel. */
  distance: number;
  /** The full list, replacing what is stored. Paths only — see `storagePaths.ts`. */
  images: PlacesToExploreImageRef[];
  enabled: boolean;
}

export interface SaveTourPackageRequest {
  hotelId: string;
  /** Omitted to add; present to edit the package with this id. */
  packageId?: string;
  name: string;
  details: string;
  /** Omitted or null for no duration; a stored one is then removed. */
  duration?: TourDuration | null;
  price: number;
  /** Omitted or null for no discount; `discount` is then ignored. */
  discountType?: TourDiscountType | null;
  discount?: number | null;
  images: PlacesToExploreImageRef[];
  enabled: boolean;
}

export interface SavePlacesToExploreItemResponse {
  hotelId: string;
  kind: PlacesToExploreKind;
  itemId: string;
  created: boolean;
}

export interface SetPlacesToExploreItemEnabledRequest {
  hotelId: string;
  kind: PlacesToExploreKind;
  itemId: string;
  enabled: boolean;
}

export interface SetPlacesToExploreItemEnabledResponse {
  hotelId: string;
  kind: PlacesToExploreKind;
  itemId: string;
  enabled: boolean;
}

export interface DeletePlacesToExploreItemRequest {
  hotelId: string;
  kind: PlacesToExploreKind;
  itemId: string;
}

export interface DeletePlacesToExploreItemResponse {
  hotelId: string;
  kind: PlacesToExploreKind;
  itemId: string;
  deleted: true;
}

/* -------------------------------------------------------------------------- */
/* getPlacesToExplore (guest)                                                 */
/* -------------------------------------------------------------------------- */

export type GetPlacesToExploreRequest = GetGuestServicesRequest;

/** A place as the guest sees it: no name key, no flag, no timestamps. */
export interface GuestPlace {
  id: string;
  name: string;
  locationDetails: string;
  shortDescription?: string;
  /** Kilometres from the hotel. */
  distance: number;
  images: PlacesToExploreImageRef[];
}

export interface GuestTourPackage {
  id: string;
  name: string;
  details: string;
  duration?: TourDuration;
  price: number;
  discountType?: TourDiscountType;
  discount?: number;
  finalPrice: number;
  images: PlacesToExploreImageRef[];
}

export interface GetPlacesToExploreResponse {
  hotelId: string;
  /** False when the service is switched off; both lists are then empty. */
  enabled: boolean;
  /** Enabled places, nearest first. */
  places: GuestPlace[];
  /** Enabled packages, in the order they were added. */
  tourPackages: GuestTourPackage[];
}
